if (!customElements.get('product-upsells')) {
  customElements.define(
    'product-upsells',
    class ProductUpsells extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-upsell-item]'));
        this.totalWrapper = this.querySelector('[data-upsell-total]');
        this.totalValue = this.querySelector('[data-upsell-total-value]');

        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onClick);

        // The Price block is re-rendered from Liquid on every variant change,
        // which wipes the total written here.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const sectionId = event?.data?.sectionId;
            if (sectionId && sectionId !== this.dataset.sectionId) return;
            this.updatePriceBlock();
          });
        }

        this.items.forEach((item) => this.syncItem(item));
        this.update();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.removeEventListener('click', this.onClick);
        this.variantChangeUnsubscriber?.();
      }

      onChange = (event) => {
        const item = event.target.closest('[data-upsell-item]');
        if (!item) return;

        if (event.target.matches('[data-upsell-variants]')) {
          this.applyVariant(item, event.target);
        }

        if (event.target.matches('[data-upsell-checkbox]') && this.isSingleSelect) {
          // Radios only fire `change` on the newly checked input, so the
          // previously selected card has to be resynced explicitly.
          this.items.forEach((other) => {
            if (other !== item) this.syncItem(other);
          });
        }

        this.syncItem(item);
        this.update();
      };

      onClick = (event) => {
        const card = event.target.closest('[data-upsell-card]');
        if (!card) return;

        // The variant picker, quantity field and audio sample own their
        // clicks; seeking a sample must not select or deselect the card.
        if (event.target.closest('select, [data-upsell-quantity], audio-player')) return;

        const item = card.closest('[data-upsell-item]');
        const checkbox = this.checkboxFor(item);
        if (!checkbox || checkbox.disabled) return;

        // A radio cannot be unchecked natively, so deselecting the current
        // choice in single-select mode has to be handled here.
        if (this.isSingleSelect && checkbox.checked) {
          event.preventDefault();
          checkbox.checked = false;
          this.syncItem(item);
          this.update();
          return;
        }

        // Otherwise let the native control and its label behave normally;
        // only bare clicks on the card need forwarding.
        if (event.target.closest('label, input, button, a')) return;

        checkbox.click();
      };

      get isSingleSelect() {
        return this.dataset.selectionMode === 'single';
      }

      get bundleEnabled() {
        return this.dataset.bundle === 'true';
      }

      get bundleLabel() {
        return this.dataset.bundleLabel || '';
      }

      /**
       * What product-form.js should do once an add including one of these
       * upsells succeeds: 'checkout', 'cart' or 'drawer'.
       */
      get afterAddBehavior() {
        return this.dataset.afterAdd || 'checkout';
      }

      checkboxFor(item) {
        return item?.querySelector('[data-upsell-checkbox]');
      }

      applyVariant(item, select) {
        const option = select.options[select.selectedIndex];
        if (!option) return;

        item.dataset.variantId = option.value;
        item.dataset.price = option.dataset.price || '0';
        item.dataset.available = option.dataset.available || 'false';

        const price = item.querySelector('[data-upsell-price]');
        if (price) price.textContent = option.dataset.priceFormatted || '';

        const comparePrice = item.querySelector('[data-upsell-compare-price]');
        if (comparePrice) {
          const compareAt = parseInt(option.dataset.comparePrice, 10);
          const current = parseInt(option.dataset.price, 10);
          const hasDiscount = Number.isFinite(compareAt) && compareAt > current;
          comparePrice.textContent = hasDiscount ? option.dataset.comparePriceFormatted : '';
          comparePrice.hidden = !hasDiscount;
        }

        const unavailable = option.dataset.available !== 'true';
        item.classList.toggle('product-upsells__item--unavailable', unavailable);

        const checkbox = this.checkboxFor(item);
        if (checkbox) {
          checkbox.disabled = unavailable;
          if (unavailable) checkbox.checked = false;
        }
      }

      syncItem(item) {
        const checkbox = this.checkboxFor(item);
        item.classList.toggle('product-upsells__item--selected', Boolean(checkbox?.checked));
      }

      quantityFor(item) {
        const input = item.querySelector('[data-upsell-quantity]');
        const quantity = parseInt(input?.value, 10);
        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }

      selectedElements() {
        return this.items.filter(
          (item) => this.checkboxFor(item)?.checked && item.dataset.available === 'true'
        );
      }

      /**
       * The selection consumed by product-form.js when building the
       * multi-item /cart/add.js payload.
       */
      getSelectedItems() {
        return this.selectedElements()
          .map((item) => ({
            id: parseInt(item.dataset.variantId, 10),
            quantity: this.quantityFor(item),
            bundle: this.bundleEnabled,
            label: item.dataset.label || '',
          }))
          .filter((item) => Number.isFinite(item.id));
      }

      /**
       * Combined price of the current selection, in cents.
       */
      selectedTotal() {
        return this.selectedElements().reduce((sum, item) => {
          const price = parseInt(item.dataset.price, 10);
          return sum + (Number.isFinite(price) ? price : 0) * this.quantityFor(item);
        }, 0);
      }

      update() {
        this.updateTotal();
        this.updatePriceBlock();
      }

      updateTotal() {
        if (!this.totalWrapper || !this.totalValue) return;

        this.totalWrapper.hidden = this.selectedElements().length === 0;
        this.totalValue.textContent = this.formatMoney(this.selectedTotal());
      }

      /**
       * The Price block of the same product-info section, looked up the way
       * global.js does so quick-add modals resolve too.
       */
      get priceBlock() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return null;

        const scope = this.closest('quick-add-modal') || this.closest('product-info') || document;
        return scope.querySelector(`#price-${sectionId} .price, #price-quickadd-${sectionId} .price`);
      }

      /**
       * Rewrites the Price block so it reads as the price of everything the
       * customer is about to add. The upsell is offered at full price, so the
       * same amount is added to the compare-at price to keep the saving shown
       * on the main variant intact.
       */
      updatePriceBlock() {
        if (this.dataset.updatePriceBlock !== 'true') return;

        const priceBlock = this.priceBlock;
        if (!priceBlock) return;

        const base = parseInt(priceBlock.dataset.priceAmount, 10);
        if (!Number.isFinite(base)) return;

        const added = this.selectedTotal();

        this.writeAmount(
          priceBlock.querySelectorAll('.price__regular .price-item--regular, .price-item--sale'),
          added > 0 ? this.formatMoney(base + added) : null
        );

        const compare = parseInt(priceBlock.dataset.compareAmount, 10);
        if (!Number.isFinite(compare) || compare <= 0) return;

        this.writeAmount(
          priceBlock.querySelectorAll('.price__sale s.price-item--regular'),
          added > 0 ? this.formatMoney(compare + added) : null
        );
      }

      /**
       * Keeps the Liquid rendered amount around so clearing the selection
       * restores its exact formatting instead of a JS approximation.
       */
      writeAmount(elements, value) {
        elements.forEach((element) => {
          if (element.dataset.upsellBaseAmount === undefined) {
            element.dataset.upsellBaseAmount = element.textContent.trim();
          }
          element.textContent = value ?? element.dataset.upsellBaseAmount;
        });
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!placeholder) return format;

        let value;
        switch (placeholder[1]) {
          case 'amount_no_decimals':
            value = this.withDelimiters(cents, 0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            value = this.withDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = this.withDelimiters(cents, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = this.withDelimiters(cents, 2, "'", '.');
            break;
          default:
            value = this.withDelimiters(cents, 2, ',', '.');
        }

        return format.replace(placeholder[0], value);
      }

      withDelimiters(cents, precision, thousands, decimal) {
        const amount = (cents / 100).toFixed(precision);
        const [whole, fraction] = amount.split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fraction ? `${grouped}${decimal}${fraction}` : grouped;
      }
    }
  );
}
